import { isEqual } from 'lodash';
import React, { useEffect } from 'react';
import { LayoutAnimation } from 'react-native';
import { View } from '@shared/components/View';
import { MBText } from '@shared/components/text';
import { MBStackedReactionsProps } from '@shared/scenes/chat/thread/components/reaction/stacked';
import { MBStackedReactionsWithList } from '@shared/scenes/chat/thread/components/reaction/stackedWithList';
import { MBSeenBy } from '@shared/scenes/chat/thread/components/seenBy';
import { Colors, grid } from '@shared/styles';
import { usePrevious } from '@shared/util/hooks';
import { notNil } from '@shared/util/notNil';

export interface MBReactionsAndSeenByProps {
  message: MBAPI.ChatMessage;
  layout: MBChatMessageLayoutOptions;
  align: 'left' | 'right';
  isDM: boolean;
  selected: MBStackedReactionsProps['selected'];
  pickerBottomMargin: MBStackedReactionsProps['pickerBottomMargin'];
  onToggleReaction: MBStackedReactionsProps['onToggleReaction'];
  marginTop?: number;
}

/*
    Reactions and list of users who have seen the message. This is placed below
    a message bubble. If there are no reactions an no one has seen the message,
    nothing is returned. When reactions change, we apply a layout animation to
    make the UI transition smoothly.
*/
export function MBReactionsAndSeenBy({
  message,
  layout,
  align,
  onToggleReaction,
  selected,
  pickerBottomMargin,
  marginTop = 0.5,
  isDM,
}: MBReactionsAndSeenByProps) {
  const prevReactions = usePrevious(message.meta.reactions);
  const anchorName = `--seen-by-${message.id}`;

  useEffect(() => {
    if (!!LayoutAnimation) {
      if (!isEqual(message.meta.reactions, prevReactions)) {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
      }
    }
  }, [message.meta.reactions, prevReactions]);

  // we show "(edited)" when the message has been edited, unless this is the last
  // message and has yet to be seen (i.e. we already prepend the "(edited)" text to "Delivered"
  // for when 0 users have seen the message, so no need to show it again here);
  // checking for !!layout.seenByUsers.length also alows for back-to-back unseen messages
  // to display "(edited)" as well
  const isEditedVisible =
    !!message.editedAt && (!layout.isUsersLastSentMessage || !!layout.seenByUsers.length);

  const isSeenByVisible = !layout.isGroupedWithBelow && !!layout.seenByUsers.length;

  // This component has a slight top padding,
  // so we only render it if there is any content
  if (!message.meta.reactions.length && !isEditedVisible && !isSeenByVisible) {
    return null;
  }

  return (
    <>
      <View paddingHorizontal={1} marginTop={marginTop}>
        <MBStackedReactionsWithList
          messageId={message.id}
          selected={selected}
          align={align}
          pickerBottomMargin={pickerBottomMargin}
          reactions={message.meta.reactions.filter(notNil)}
          onToggleReaction={onToggleReaction}
        />
      </View>
      <View
        row
        centerVertically
        paddingRight={align === 'right' ? 1.75 : 0}
        paddingLeft={align === 'left' ? 1.75 : 0}
      >
        {isEditedVisible && (
          <MBText style={{ fontSize: 10, marginRight: grid(1) }} color={Colors.gray.normal}>
            (edited)
          </MBText>
        )}

        {/* we show images only if the message is the last one */}
        {isSeenByVisible && (
          <MBSeenBy
            align={align}
            users={layout.seenByUsers}
            sender={message.sender}
            showImages={layout.isLastMessage}
            isSeenByCountHidden={isDM}
            anchorName={anchorName}
          />
        )}
      </View>
    </>
  );
}
